import PropTypes from 'prop-types';
import React, { useState } from 'react';

// The threshold sentence, plus a second level of disclosure behind "Adjust". The sentence
// states the rule in words with the live saved value in it; the select and its warning only
// appear for someone who came to change it.
//
// Disclosure state lives here, one instance per card, so opening one card's Adjust does not
// open another's — and because the row unmounts when the rule is switched off, re-enabling
// a rule starts collapsed again.
const ThresholdRow = ({ ruleType, threshold, value, options, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const panelId = `threshold-${ruleType}`;
  // A value saved before an option was retired (min_events 2) is still rendered, so the
  // select never shows blank. The server normalises it on the next write.
  const choices = options.includes(value) ? options : [value, ...options];

  return (
    <div className="automation-rules__threshold">
      <span>
        {threshold.before}{' '}
        <strong className="automation-rules__threshold-value">{threshold.label(value)}</strong>{' '}
        {threshold.after}
      </span>
      <button
        type="button"
        className="automation-rules__adjust"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={threshold.adjustLabel}
        disabled={disabled}
        data-testid={`threshold-adjust-${ruleType}`}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        Adjust
        <span
          aria-hidden="true"
          className={`automation-rules__adjust-chevron${
            open ? ' automation-rules__adjust-chevron--open' : ''
          }`}
        >
          ⌄
        </span>
      </button>
      {open ? (
        <div className="automation-rules__adjust-panel" id={panelId}>
          <select
            className="automation-rules__select"
            aria-label={threshold.adjustLabel}
            value={value}
            disabled={disabled}
            data-testid={`threshold-select-${ruleType}`}
            onChange={(event) => onChange(Number(event.target.value))}
          >
            {choices.map((option) => (
              <option key={option} value={option}>
                {threshold.label(option)}
              </option>
            ))}
          </select>
          <p className="automation-rules__guidance">{threshold.guidance}</p>
        </div>
      ) : null}
    </div>
  );
};

ThresholdRow.propTypes = {
  ruleType: PropTypes.string.isRequired,
  threshold: PropTypes.shape({
    before: PropTypes.string.isRequired,
    after: PropTypes.string.isRequired,
    label: PropTypes.func.isRequired,
    adjustLabel: PropTypes.string.isRequired,
    guidance: PropTypes.string.isRequired,
  }).isRequired,
  value: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.number).isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

ThresholdRow.defaultProps = { disabled: false };

export default ThresholdRow;
